import React, { useState, useEffect } from 'react'
import { useAuth } from '../../contexts/AuthContext'
import { backendService } from '../../services/backendService'
import { logInfo, logError } from '../../utils/errorLogger'
import './ProfileView.css'

const ProfileView: React.FC = () => {
  const { doctorName, isAdmin } = useAuth()

  // ── Token balances ──
  const [tokenBalance, setTokenBalance] = useState<number | null>(null)
  const [aiTokenBalance, setAiTokenBalance] = useState<number | null>(null)
  const [loadingToken, setLoadingToken] = useState(true)
  const [loadingAiToken, setLoadingAiToken] = useState(true)
  const [error, setError] = useState<string | null>(null)

  // ── Profile form ──
  const [profile, setProfile] = useState<Record<string, any>>({})
  const [profileEdited, setProfileEdited] = useState(false)
  const [savingProfile, setSavingProfile] = useState(false)
  const [profileMsg, setProfileMsg] = useState<string | null>(null)
  const [profileMsgType, setProfileMsgType] = useState<'success' | 'error'>('success')

  // ── Password form ──
  const [oldPassword, setOldPassword] = useState('')
  const [newPassword, setNewPassword] = useState('')
  const [newPasswordConfirm, setNewPasswordConfirm] = useState('')
  const [changingPassword, setChangingPassword] = useState(false)
  const [pwMsg, setPwMsg] = useState<string | null>(null)
  const [pwMsgType, setPwMsgType] = useState<'success' | 'error'>('success')
  const [showPwForm, setShowPwForm] = useState(false)

  // ── Fetch profile & balances on mount ──
  useEffect(() => {
    let cancelled = false

    async function load() {
      try {
        const [profileRes, tokens, aiTokens] = await Promise.allSettled([
          backendService.getProfile(),
          backendService.getTokenBalance(),
          backendService.getMyAiTokenBalance(),
        ])

        if (cancelled) return

        if (profileRes.status === 'fulfilled') {
          setProfile(profileRes.value.user || profileRes.value)
        } else {
          logError('Failed to fetch profile', 'ProfileView', profileRes.reason)
        }

        if (tokens.status === 'fulfilled') {
          setTokenBalance(tokens.value.balance)
          setLoadingToken(false)
        } else {
          setLoadingToken(false)
        }

        if (aiTokens.status === 'fulfilled') {
          setAiTokenBalance(aiTokens.value.balance)
          setLoadingAiToken(false)
        } else {
          setLoadingAiToken(false)
        }
      } catch (err) {
        if (!cancelled) {
          setError(err instanceof Error ? err.message : 'Failed to load data')
          setLoadingToken(false)
          setLoadingAiToken(false)
        }
      }
    }

    load()
    logInfo('ProfileView mounted', 'ProfileView')

    return () => { cancelled = true }
  }, [])

  // ── Profile field change ──
  const handleProfileChange = (field: string, value: string) => {
    setProfile((prev: Record<string, any>) => ({ ...prev, [field]: value }))
    setProfileEdited(true)
    setProfileMsg(null)
  }

  // ── Save profile ──
  const handleSaveProfile = async () => {
    setSavingProfile(true)
    setProfileMsg(null)
    try {
      const res = await backendService.updateProfile(profile)
      setProfile(res.user || res)
      setProfileEdited(false)
      setProfileMsg('Profile updated successfully.')
      setProfileMsgType('success')
    } catch (err: any) {
      const detail = err?.message || 'Failed to update profile.'
      setProfileMsg(detail)
      setProfileMsgType('error')
    } finally {
      setSavingProfile(false)
    }
  }

  // ── Change password ──
  const handleChangePassword = async () => {
    if (!oldPassword || !newPassword || !newPasswordConfirm) {
      setPwMsg('All password fields are required.')
      setPwMsgType('error')
      return
    }
    if (newPassword !== newPasswordConfirm) {
      setPwMsg('New passwords do not match.')
      setPwMsgType('error')
      return
    }
    if (newPassword.length < 8) {
      setPwMsg('New password must be at least 8 characters.')
      setPwMsgType('error')
      return
    }
    setChangingPassword(true)
    setPwMsg(null)
    try {
      await backendService.changePassword(oldPassword, newPassword, newPasswordConfirm)
      setPwMsg('Password changed successfully! Please log in again.')
      setPwMsgType('success')
      setOldPassword('')
      setNewPassword('')
      setNewPasswordConfirm('')
      setShowPwForm(false)
    } catch (err: any) {
      const detail = err?.message || 'Failed to change password.'
      setPwMsg(detail)
      setPwMsgType('error')
    } finally {
      setChangingPassword(false)
    }
  }

  const roleLabel = isAdmin ? 'Administrator' : 'Doctor'
  const roleClass = isAdmin ? 'admin' : 'doctor'

  // ── Editable profile fields config ──
  const profileFields: { key: string; label: string; type?: string }[] = [
    { key: 'name', label: 'Full Name' },
    { key: 'phone', label: 'Phone' },
    { key: 'address', label: 'Address' },
    { key: 'zip_code', label: 'ZIP Code' },
    { key: 'company_name', label: 'Company Name' },
    { key: 'vat_number', label: 'VAT Number' },
    { key: 'billing_address', label: 'Billing Address' },
  ]

  return (
    <div className="profile-view">
      {/* ── User Info Card ── */}
      <div className="profile-user-card">
        <div className="profile-user-row">
          <div className="profile-avatar">
            {(doctorName || 'U').charAt(0).toUpperCase()}
          </div>
          <div>
            <h2 className="profile-name">{doctorName || 'User'}</h2>
            <span className={`profile-role-badge ${roleClass}`}>
              {roleLabel}
            </span>
          </div>
        </div>
        <div className="profile-details">
          <div>
            <div className="profile-detail-label">Account Type</div>
            <div className="profile-detail-value">Full Access</div>
          </div>
          <div>
            <div className="profile-detail-label">Role</div>
            <div className="profile-detail-value">{roleLabel}</div>
          </div>
        </div>
      </div>

      {/* ── Token Balances ── */}
      <div className="profile-token-grid">
        <div className="profile-token-card">
          <div className="profile-token-header">
            <div className="profile-token-icon cyan">
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
              </svg>
            </div>
            <span className="profile-token-badge">Balance</span>
          </div>
          <div className="profile-token-label">Token Balance</div>
          {loadingToken ? (
            <div className="profile-token-loading">
              <div className="profile-token-spinner" />
            </div>
          ) : error && tokenBalance === null ? (
            <div className="profile-token-error">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Failed to load
            </div>
          ) : (
            <p className="profile-token-value cyan">
              {tokenBalance !== null ? tokenBalance.toLocaleString() : '0'}
            </p>
          )}
        </div>

        <div className="profile-token-card">
          <div className="profile-token-header">
            <div className="profile-token-icon sky">
              <svg width="18" height="18" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M9.813 15.904L9 18.75l-.813-2.846a4.5 4.5 0 00-3.09-3.09L2.25 12l2.846-.813a4.5 4.5 0 003.09-3.09L9 5.25l.813 2.846a4.5 4.5 0 003.09 3.09L15.75 12l-2.846.813a4.5 4.5 0 00-3.09 3.09zM18.259 8.715L18 9.75l-.259-1.035a3.375 3.375 0 00-2.455-2.456L14.25 6l1.036-.259a3.375 3.375 0 002.455-2.456L18 2.25l.259 1.035a3.375 3.375 0 002.455 2.456L21.75 6l-1.036.259a3.375 3.375 0 00-2.455 2.456zM16.894 20.567L16.5 21.75l-.394-1.183a2.25 2.25 0 00-1.423-1.423L13.5 18.75l1.183-.394a2.25 2.25 0 001.423-1.423l.394-1.183.394 1.183a2.25 2.25 0 001.423 1.423l1.183.394-1.183.394a2.25 2.25 0 00-1.423 1.423z" />
              </svg>
            </div>
            <span className="profile-token-badge">Balance</span>
          </div>
          <div className="profile-token-label">AI Token Balance</div>
          {loadingAiToken ? (
            <div className="profile-token-loading">
              <div className="profile-token-spinner" />
            </div>
          ) : error && aiTokenBalance === null ? (
            <div className="profile-token-error">
              <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                <path strokeLinecap="round" strokeLinejoin="round" d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.964-.833-2.732 0L4.082 16.5c-.77.833.192 2.5 1.732 2.5z" />
              </svg>
              Failed to load
            </div>
          ) : (
            <p className="profile-token-value sky">
              {aiTokenBalance !== null ? aiTokenBalance.toLocaleString() : '0'}
            </p>
          )}
        </div>
      </div>

      {/* ── Account Settings ── */}
      <div style={{ marginTop: 24, background: '#ffffff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
        <h3 style={{ margin: '0 0 16px', fontSize: '1.1rem', fontWeight: 600, color: '#0f172a' }}>
          Account Settings
        </h3>

        {profileMsg && (
          <div style={{
            padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: '0.85rem',
            background: profileMsgType === 'success' ? '#f0fdf4' : '#fef2f2',
            color: profileMsgType === 'success' ? '#166534' : '#991b1b',
            border: `1px solid ${profileMsgType === 'success' ? '#bbf7d0' : '#fecaca'}`,
          }}>
            {profileMsg}
          </div>
        )}

        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12, marginBottom: 16 }}>
          {profileFields.map(f => (
            <div key={f.key}>
              <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                {f.label}
              </label>
              <input
                type={f.type || 'text'}
                value={profile[f.key] || ''}
                onChange={e => handleProfileChange(f.key, e.target.value)}
                style={{
                  width: '100%', padding: '6px 10px', fontSize: '0.85rem',
                  border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none',
                  color: '#0f172a', background: '#ffffff',
                  boxSizing: 'border-box',
                }}
              />
            </div>
          ))}
        </div>

        <div style={{ display: 'flex', gap: 10, alignItems: 'center' }}>
          <button
            type="button"
            onClick={handleSaveProfile}
            disabled={!profileEdited || savingProfile}
            style={{
              fontSize: '0.85rem', padding: '8px 18px',
              border: 'none', borderRadius: 6, cursor: profileEdited ? 'pointer' : 'not-allowed',
              fontWeight: 600, background: profileEdited ? '#06b6d4' : '#cbd5e1',
              color: '#ffffff', opacity: savingProfile ? 0.6 : 1,
            }}
          >
            {savingProfile ? 'Saving...' : 'Save Changes'}
          </button>
          {profileEdited && (
            <span style={{ fontSize: '0.78rem', color: '#f59e0b' }}>
              You have unsaved changes.
            </span>
          )}
        </div>
      </div>

      {/* ── Change Password ── */}
      <div style={{ marginTop: 16, background: '#ffffff', borderRadius: 12, border: '1px solid #e2e8f0', padding: 20 }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: showPwForm ? 16 : 0 }}>
          <h3 style={{ margin: 0, fontSize: '1.1rem', fontWeight: 600, color: '#0f172a' }}>
            Change Password
          </h3>
          <button
            type="button"
            onClick={() => { setShowPwForm(!showPwForm); setPwMsg(null) }}
            style={{
              fontSize: '0.85rem', padding: '6px 14px',
              border: '1px solid #cbd5e1', borderRadius: 6, cursor: 'pointer',
              fontWeight: 500, background: '#ffffff', color: '#475569',
            }}
          >
            {showPwForm ? 'Cancel' : 'Change Password'}
          </button>
        </div>

        {showPwForm && (
          <>
            {pwMsg && (
              <div style={{
                padding: '8px 12px', borderRadius: 6, marginBottom: 12, fontSize: '0.85rem',
                background: pwMsgType === 'success' ? '#f0fdf4' : '#fef2f2',
                color: pwMsgType === 'success' ? '#166534' : '#991b1b',
                border: `1px solid ${pwMsgType === 'success' ? '#bbf7d0' : '#fecaca'}`,
              }}>
                {pwMsg}
              </div>
            )}

            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fill, minmax(220px, 1fr))', gap: 12 }}>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                  Current Password
                </label>
                <input
                  type="password"
                  value={oldPassword}
                  onChange={e => setOldPassword(e.target.value)}
                  placeholder="••••••••"
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: '0.85rem',
                    border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none',
                    color: '#0f172a', background: '#ffffff',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                  New Password
                </label>
                <input
                  type="password"
                  value={newPassword}
                  onChange={e => setNewPassword(e.target.value)}
                  placeholder="Min 8 characters"
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: '0.85rem',
                    border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none',
                    color: '#0f172a', background: '#ffffff',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
              <div>
                <label style={{ display: 'block', fontSize: '0.75rem', fontWeight: 600, color: '#64748b', marginBottom: 4 }}>
                  Confirm New Password
                </label>
                <input
                  type="password"
                  value={newPasswordConfirm}
                  onChange={e => setNewPasswordConfirm(e.target.value)}
                  placeholder="Re-type new password"
                  style={{
                    width: '100%', padding: '6px 10px', fontSize: '0.85rem',
                    border: '1px solid #e2e8f0', borderRadius: 6, outline: 'none',
                    color: '#0f172a', background: '#ffffff',
                    boxSizing: 'border-box',
                  }}
                />
              </div>
            </div>

            <div style={{ marginTop: 12 }}>
              <button
                type="button"
                onClick={handleChangePassword}
                disabled={changingPassword}
                style={{
                  fontSize: '0.85rem', padding: '8px 18px',
                  border: 'none', borderRadius: 6, cursor: 'pointer',
                  fontWeight: 600, background: '#06b6d4', color: '#ffffff',
                  opacity: changingPassword ? 0.6 : 1,
                }}
              >
                {changingPassword ? 'Changing...' : 'Update Password'}
              </button>
            </div>
          </>
        )}
      </div>

      {/* Info Banner */}
      <div className="profile-info-banner">
        <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
          <path strokeLinecap="round" strokeLinejoin="round" d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
        </svg>
        Token balances are fetched fresh on each visit.
      </div>
    </div>
  )
}

export default ProfileView
